/**
 * ============================================================================
 * USER PAGE: Tiny URL Telemetry & Analytics Dashboard
 * ============================================================================
 * Purpose:
 *   Dedicated full-screen analytics page for a specific Tiny URL:
 *   - Overview of total clicks, devices, browsers, referrers, locations
 *   - Visual progress distribution cards
 *   - Paginated Click Audit Logs table with masked IPs and subpaths
 * ============================================================================
 */

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  LinkIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentCheckIcon,
  ArrowTopRightOnSquareIcon,
  ClockIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
  GlobeAmericasIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  ChartBarIcon,
  CalendarDaysIcon,
  PencilSquareIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

import tinyUrlService from "../../services/tinyUrlService";
import { getCurrentUser, hasPermission } from "../../../core/utils/auth";

export default function TinyUrlAnalytics() {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = getCurrentUser();

  const isPermitted = hasPermission(currentUser, "TINY_URL_MANAGEMENT");

  const [link, setLink] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [clickLogs, setClickLogs] = useState([]);
  const [logsPagination, setLogsPagination] = useState({ page: 1, limit: 15, total: 0, totalPages: 1 });

  const [loading, setLoading] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [copied, setCopied] = useState(false);

  // Derive frontend short URL
  const frontendBase = window.location.origin;
  const shortUrl = link?.code ? `${frontendBase}/${link.code}` : "";

  // Fetch Link & Analytics
  const loadAnalytics = useCallback(async () => {
    if (!id || !isPermitted) return;
    setLoading(true);
    try {
      const [analyticsRes, logsRes] = await Promise.all([
        tinyUrlService.getAnalytics(id),
        tinyUrlService.getClicks(id, { page: 1, limit: logsPagination.limit }),
      ]);

      if (analyticsRes && analyticsRes.success) {
        setAnalytics(analyticsRes.data);
        if (analyticsRes.data?.link) {
          setLink(analyticsRes.data.link);
        }
      }
      if (logsRes && logsRes.success) {
        setClickLogs(logsRes.data || []);
        if (logsRes.pagination) {
          setLogsPagination(logsRes.pagination);
        }
      }
    } catch (err) {
      console.error("Failed to load analytics:", err);
      Swal.fire({
        title: "Failed to Load Analytics",
        text: err.response?.data?.error || err.message,
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [id, isPermitted, logsPagination.limit]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  // Fetch paginated click logs
  const handlePageChange = async (newPage) => {
    if (!id || newPage < 1 || newPage > logsPagination.totalPages) return;
    setLoadingLogs(true);
    try {
      const res = await tinyUrlService.getClicks(id, {
        page: newPage,
        limit: logsPagination.limit,
      });
      if (res && res.success) {
        setClickLogs(res.data || []);
        if (res.pagination) {
          setLogsPagination(res.pagination);
        }
      }
    } catch (err) {
      console.error("Failed to change logs page:", err);
    } finally {
      setLoadingLogs(false);
    }
  };

  // Copy Short URL Helper
  const handleCopyLink = () => {
    if (!shortUrl) return;
    navigator.clipboard.writeText(shortUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Toggle Status
  const handleToggleStatus = async () => {
    if (!link) return;
    try {
      const newStatus = !link.isActive;
      await tinyUrlService.toggleStatus(link._id, newStatus);
      setLink((prev) => ({ ...prev, isActive: newStatus }));
      Swal.fire({
        title: "Status Updated",
        text: `Link is now ${newStatus ? "Active" : "Inactive"}`,
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        title: "Status Toggle Failed",
        text: err.response?.data?.error || err.message,
        icon: "error",
      });
    }
  };

  if (!isPermitted) {
    return (
      <div className="p-8 max-w-lg mx-auto text-center mt-12 bg-white dark:bg-gray-800 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-700">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">403 - Forbidden</h2>
        <p className="mt-2 text-sm text-gray-500">
          You do not have permission to view this Tiny URL&apos;s analytics.
        </p>
        <button
          onClick={() => navigate("/workspace/tiny-urls")}
          className="mt-6 px-5 py-2.5 rounded-xl bg-[#3a57e8] text-white text-xs font-semibold cursor-pointer shadow-md"
        >
          Return to Tiny URLs
        </button>
      </div>
    );
  }

  const isExpired =
    link?.expirationDate && new Date(link.expirationDate).getTime() <= Date.now();

  return (
    <div className="space-y-6 max-w-[1600px] mx-auto">
      {/* Top Breadcrumb & Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/workspace/tiny-urls")}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-xs font-semibold shadow-xs cursor-pointer transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to Tiny URLs
          </button>
          <div className="h-4 w-px bg-gray-300 dark:bg-gray-700" />
          <span className="text-xs text-gray-400">Telemetry & Redirection Analytics</span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={loadAnalytics}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-xs font-semibold shadow-xs cursor-pointer transition-colors"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? "animate-spin text-[#3a57e8]" : ""}`} />
            Refresh
          </button>
          <a
            href={shortUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#3a57e8] hover:bg-[#2e47d4] text-white text-xs font-semibold shadow-md shadow-[#3a57e8]/30 cursor-pointer transition-all duration-150"
          >
            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
            Test Redirect
          </a>
        </div>
      </div>

      {/* Main Link Header Card */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 sm:p-6 border border-gray-200/80 dark:border-gray-700/80 shadow-xs">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-mono text-base sm:text-lg font-bold text-[#3a57e8] bg-blue-50 dark:bg-blue-900/40 px-3.5 py-1 rounded-xl border border-blue-200/60 dark:border-blue-800/40">
                {link?.code || "..."}
              </span>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                {link?.title || "Untitled Link"}
              </h1>

              {/* Status Badge */}
              <button
                onClick={handleToggleStatus}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold cursor-pointer transition-all ${
                  isExpired
                    ? "bg-red-50 text-red-600 dark:bg-red-950/40 border border-red-200"
                    : link?.isActive
                    ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 border border-emerald-200"
                    : "bg-amber-50 text-amber-700 dark:bg-amber-950/50 border border-amber-200"
                }`}
                title="Click to toggle status"
              >
                <span
                  className={`w-2 h-2 rounded-full ${
                    isExpired
                      ? "bg-red-500"
                      : link?.isActive
                      ? "bg-emerald-500 animate-pulse"
                      : "bg-amber-500"
                  }`}
                />
                {isExpired ? "Expired" : link?.isActive ? "Active" : "Inactive"}
              </button>
            </div>

            {/* Target Destination & Description */}
            <div className="mt-3 space-y-1.5 text-xs text-gray-500 dark:text-gray-400">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-gray-700 dark:text-gray-300">Target Destination:</span>
                <span className="font-mono text-gray-800 dark:text-gray-200 break-all select-all">
                  {link?.originalDestinationUrl || "-"}
                </span>
              </div>
              {link?.description && (
                <p className="text-gray-600 dark:text-gray-300 italic">{link.description}</p>
              )}
            </div>
          </div>

          {/* Quick Copy Box */}
          <div className="bg-gray-50 dark:bg-gray-750 p-3 rounded-2xl border border-gray-200 dark:border-gray-700 flex flex-col gap-2 min-w-[280px]">
            <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
              Frontend Short Link
            </span>
            <div className="flex items-center justify-between gap-2 bg-white dark:bg-gray-800 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-600">
              <span className="font-mono text-xs font-bold text-[#3a57e8] truncate">
                {shortUrl}
              </span>
              <button
                onClick={handleCopyLink}
                className="text-gray-400 hover:text-[#3a57e8] p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                title="Copy Short URL"
              >
                {copied ? (
                  <ClipboardDocumentCheckIcon className="w-4 h-4 text-emerald-500" />
                ) : (
                  <ClipboardDocumentIcon className="w-4 h-4" />
                )}
              </button>
            </div>
            <div className="flex items-center justify-between text-[11px] text-gray-400">
              <span>Created: {link?.createdAt ? new Date(link.createdAt).toLocaleDateString() : "-"}</span>
              <span>Expires: {link?.expirationDate ? new Date(link.expirationDate).toLocaleDateString() : "Never"}</span>
            </div>
          </div>
        </div>
      </div>

      {/* 4 Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-5 border border-gray-200/80 dark:border-gray-700/80 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Clicks</span>
            <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-[#3a57e8] flex items-center justify-center">
              <ChartBarIcon className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3 text-3xl font-extrabold text-[#3a57e8]">
            {analytics?.totalClicks?.toLocaleString() || 0}
          </div>
          <span className="text-[11px] text-gray-400">All recorded redirections</span>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-5 border border-gray-200/80 dark:border-gray-700/80 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Unique Referrers</span>
            <div className="w-9 h-9 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 flex items-center justify-center">
              <GlobeAmericasIcon className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3 text-3xl font-extrabold text-emerald-600 dark:text-emerald-400">
            {analytics?.referrers?.length || 0}
          </div>
          <span className="text-[11px] text-gray-400">Traffic origin domains</span>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-5 border border-gray-200/80 dark:border-gray-700/80 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Top Device</span>
            <div className="w-9 h-9 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 flex items-center justify-center">
              <DevicePhoneMobileIcon className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3 text-2xl font-bold text-indigo-600 dark:text-indigo-400 truncate">
            {analytics?.devices?.[0]?.name || "N/A"}
          </div>
          <span className="text-[11px] text-gray-400">
            {analytics?.devices?.[0]?.count ? `${analytics.devices[0].count} clicks` : "No visits"}
          </span>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-5 border border-gray-200/80 dark:border-gray-700/80 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Top Browser</span>
            <div className="w-9 h-9 rounded-xl bg-purple-50 dark:bg-purple-900/30 text-purple-600 flex items-center justify-center">
              <ComputerDesktopIcon className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3 text-2xl font-bold text-purple-600 dark:text-purple-400 truncate">
            {analytics?.browsers?.[0]?.name || "N/A"}
          </div>
          <span className="text-[11px] text-gray-400">
            {analytics?.browsers?.[0]?.count ? `${analytics.browsers[0].count} clicks` : "No visits"}
          </span>
        </div>
      </div>

      {/* Visual Distributions Breakdown Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
        {/* Devices */}
        <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-200/80 dark:border-gray-700/80 shadow-xs">
          <h3 className="font-bold text-sm text-gray-800 dark:text-gray-100 mb-3 flex items-center gap-2">
            <DevicePhoneMobileIcon className="w-4 h-4 text-[#3a57e8]" />
            Devices
          </h3>
          {analytics?.devices?.length === 0 ? (
            <p className="text-gray-400 py-6 text-center">No clicks recorded yet</p>
          ) : (
            <div className="space-y-3">
              {analytics?.devices?.map((d) => {
                const total = analytics?.totalClicks || 1;
                const pct = Math.round((d.count / total) * 100);
                return (
                  <div key={d.name}>
                    <div className="flex justify-between text-xs mb-1 font-semibold text-gray-700 dark:text-gray-200">
                      <span>{d.name}</span>
                      <span>{d.count} ({pct}%)</span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                      <div className="h-full bg-[#3a57e8] rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Browsers */}
        <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-200/80 dark:border-gray-700/80 shadow-xs">
          <h3 className="font-bold text-sm text-gray-800 dark:text-gray-100 mb-3 flex items-center gap-2">
            <ComputerDesktopIcon className="w-4 h-4 text-indigo-500" />
            Browsers
          </h3>
          {analytics?.browsers?.length === 0 ? (
            <p className="text-gray-400 py-6 text-center">No clicks recorded yet</p>
          ) : (
            <div className="space-y-3">
              {analytics?.browsers?.map((b) => {
                const total = analytics?.totalClicks || 1;
                const pct = Math.round((b.count / total) * 100);
                return (
                  <div key={b.name}>
                    <div className="flex justify-between text-xs mb-1 font-semibold text-gray-700 dark:text-gray-200">
                      <span>{b.name}</span>
                      <span>{b.count} ({pct}%)</span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                      <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Operating Systems */}
        <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-200/80 dark:border-gray-700/80 shadow-xs">
          <h3 className="font-bold text-sm text-gray-800 dark:text-gray-100 mb-3 flex items-center gap-2">
            <ShieldCheckIcon className="w-4 h-4 text-emerald-500" />
            Operating Systems
          </h3>
          {analytics?.os?.length === 0 ? (
            <p className="text-gray-400 py-6 text-center">No clicks recorded yet</p>
          ) : (
            <div className="space-y-3">
              {analytics?.os?.map((o) => {
                const total = analytics?.totalClicks || 1;
                const pct = Math.round((o.count / total) * 100);
                return (
                  <div key={o.name}>
                    <div className="flex justify-between text-xs mb-1 font-semibold text-gray-700 dark:text-gray-200">
                      <span>{o.name}</span>
                      <span>{o.count} ({pct}%)</span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Referrer Sources */}
        <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-200/80 dark:border-gray-700/80 shadow-xs">
          <h3 className="font-bold text-sm text-gray-800 dark:text-gray-100 mb-3 flex items-center gap-2">
            <GlobeAmericasIcon className="w-4 h-4 text-amber-500" />
            Referrers
          </h3>
          {analytics?.referrers?.length === 0 ? (
            <p className="text-gray-400 py-6 text-center">No clicks recorded yet</p>
          ) : (
            <div className="space-y-3">
              {analytics?.referrers?.map((r) => {
                const total = analytics?.totalClicks || 1;
                const pct = Math.round((r.count / total) * 100);
                return (
                  <div key={r.name}>
                    <div className="flex justify-between text-xs mb-1 font-semibold text-gray-700 dark:text-gray-200">
                      <span>{r.name}</span>
                      <span>{r.count} ({pct}%)</span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                      <div className="h-full bg-amber-500 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Detailed Click Audit Logs Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/80 dark:border-gray-700/80 shadow-xs overflow-hidden">
        <div className="p-4 sm:p-5 bg-gray-50/50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-bold text-gray-900 dark:text-white">
              Detailed Visitor Click Audit Logs
            </h3>
            <p className="text-[11px] text-gray-400">
              Telemetry captured asynchronously on each HTTP 302 redirection (IPs masked for privacy)
            </p>
          </div>
          <span className="text-xs font-mono font-semibold text-gray-500 dark:text-gray-300">
            {logsPagination.total} Total Records
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-[#f5f6fa] dark:bg-gray-750 text-gray-600 dark:text-gray-300 uppercase tracking-wider font-semibold border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="py-3 px-4">Timestamp</th>
                <th className="py-3 px-4">Masked IP</th>
                <th className="py-3 px-4">Device & OS</th>
                <th className="py-3 px-4">Browser</th>
                <th className="py-3 px-4">Forwarded Subpath</th>
                <th className="py-3 px-4">Location</th>
                <th className="py-3 px-4">Referrer</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
              {loadingLogs ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-400">
                    <ArrowPathIcon className="w-6 h-6 mx-auto animate-spin text-[#3a57e8] mb-2" />
                    Loading click logs...
                  </td>
                </tr>
              ) : clickLogs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-400">
                    No visitor click logs captured yet. Try testing your short link!
                  </td>
                </tr>
              ) : (
                clickLogs.map((log) => (
                  <tr key={log._id} className="hover:bg-blue-50/30 dark:hover:bg-gray-750/40 transition-colors">
                    <td className="py-3 px-4 whitespace-nowrap text-gray-500 text-[11px]">
                      {new Date(log.dateTime).toLocaleString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      })}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap font-mono font-semibold text-gray-700 dark:text-gray-300">
                      {log.ipMasked || "xxx.xxx.1.1"}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span className="font-semibold text-gray-800 dark:text-gray-200">
                        {log.device}
                      </span>{" "}
                      <span className="text-gray-400">({log.os})</span>
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap text-gray-700 dark:text-gray-300">
                      {log.browser}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap font-mono text-[11px] text-[#3a57e8] dark:text-blue-400">
                      {log.urlPart || "-"}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap text-gray-600 dark:text-gray-300">
                      {log.location || log.country || "Local Network"}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap text-gray-500">
                      {log.referrer || "Direct"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Logs Pagination Footer */}
        {logsPagination.totalPages > 1 && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between text-xs text-gray-500">
            <div>
              Page {logsPagination.page} of {logsPagination.totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                disabled={logsPagination.page <= 1 || loadingLogs}
                onClick={() => handlePageChange(logsPagination.page - 1)}
                className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 font-semibold"
              >
                Previous
              </button>
              <button
                disabled={logsPagination.page >= logsPagination.totalPages || loadingLogs}
                onClick={() => handlePageChange(logsPagination.page + 1)}
                className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 font-semibold"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
